import {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useMutation, useQuery} from 'react-query'
import {
  getConversationInvite,
  getFile,
  getToken,
  joinConversationInvite,
} from 'api'
import {notifyErrorOccured} from 'utils/toastNotifications'

export const PENDING_INVITE_KEY = 'pendingCircleInvite'

/**
 * Public landing page for a Circle invite link (/join/:token) — the page
 * behind the QR codes handed out at cohort demos. Renders the group
 * preview with NO login wall; joining either happens immediately (signed
 * in) or stashes the token and routes through register/login, after which
 * PendingInviteRedeemer (App.js) completes the join.
 */
const JoinCircle = () => {
  const {token} = useParams()
  const navigate = useNavigate()
  const isAuth = Boolean(getToken()?.token)
  const [avatarUrl, setAvatarUrl] = useState('')

  const {data, isLoading} = useQuery(
    ['circleInvite', token],
    () => getConversationInvite(token),
    {retry: 1, refetchOnWindowFocus: false}
  )

  const group = data?.group

  useEffect(() => {
    let cancelled = false
    if (group?.avatarMediaId) {
      getFile({id: group.avatarMediaId, type: 'post_media'})
        .then((file) => {
          if (!cancelled && file?.url) setAvatarUrl(file.url)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [group?.avatarMediaId])

  const {mutate: joinNow, isLoading: joining} = useMutation(
    () => joinConversationInvite(token),
    {
      onSuccess: (result) => {
        if (result?.id) {
          navigate(`/messages/${result.id}`)
        } else {
          notifyErrorOccured({message: 'This invite link is no longer valid'})
        }
      },
      onError: (error) => {
        notifyErrorOccured({
          message:
            error?.response?.data?.message || 'Could not join this Circle',
        })
      },
    }
  )

  const stashAndGo = (path) => {
    try {
      localStorage.setItem(PENDING_INVITE_KEY, token)
    } catch {
      // Storage unavailable — the user can reopen the link after signup.
    }
    navigate(path)
  }

  const invalidCopy =
    data?.reason === 'expired'
      ? 'This invite link has expired. Ask a Circle admin for a fresh one.'
      : data?.reason === 'exhausted'
        ? 'This invite link has reached its member limit. Ask a Circle admin for a fresh one.'
        : 'This invite link is no longer valid. Ask whoever shared it for a fresh one.'

  return (
    <div className="min-h-[100dvh] bg-sharpenn-cream flex flex-col items-center px-5">
      <p className="font-display font-extrabold text-xl text-brand mt-14 tracking-tight">
        Sharpenn
      </p>

      <div className="mt-9 w-full max-w-[400px] bg-white border border-sharpenn-blush rounded-[20px] shadow-subtle-md px-6 py-8 flex flex-col items-center">
        {isLoading ? (
          <p className="text-sm text-gray-400 py-16">Loading invite…</p>
        ) : !data || data.valid === false ? (
          <>
            <div className="w-16 h-16 rounded-2xl bg-sharpenn-blush flex items-center justify-center">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#C4783A"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="9" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="mt-4 font-display font-extrabold text-lg text-sharpenn-black text-center">
              Invite not available
            </p>
            <p className="mt-2 text-sm text-sharpenn-slate text-center leading-relaxed">
              {invalidCopy}
            </p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-6 h-11 px-6 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-sm transition-colors"
            >
              Explore Sharpenn
            </button>
          </>
        ) : (
          <>
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={group.name}
                className="w-24 h-24 rounded-[28px] object-cover"
              />
            ) : (
              <div
                className="w-24 h-24 rounded-[28px] flex items-center justify-center"
                style={{
                  background: 'linear-gradient(135deg, #E8913A, #C4783A)',
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
            )}

            <span className="mt-4 bg-brand-subtle text-brand-warm text-[11.5px] font-bold uppercase tracking-wider rounded-full px-3 py-1">
              You're invited
            </span>

            <h1 className="mt-3 font-display font-extrabold text-2xl text-sharpenn-black text-center tracking-tight">
              {group.name}
            </h1>
            <p className="mt-1.5 text-[13.5px] text-sharpenn-slate">
              {group.memberCount} member{group.memberCount === 1 ? '' : 's'}
              {group.createdByName
                ? ` · Created by ${group.createdByName}`
                : ''}
            </p>

            {group.description && (
              <p className="mt-4 text-sm text-sharpenn-slate text-center leading-relaxed">
                {group.description}
              </p>
            )}

            <button
              type="button"
              disabled={joining}
              onClick={() => (isAuth ? joinNow() : stashAndGo('/register'))}
              className="mt-6 w-full h-[52px] rounded-full bg-brand hover:bg-brand-hover disabled:opacity-60 text-white font-bold text-base transition-colors"
            >
              {joining ? 'Joining…' : 'Join this Circle'}
            </button>

            {!isAuth && (
              <>
                <p className="mt-3 text-[12.5px] text-gray-400 text-center">
                  Joining creates your free Sharpenn account — it takes a
                  minute.
                </p>
                <div className="mt-5 w-full h-px bg-sharpenn-blush" />
                <p className="mt-4 text-[13.5px] text-sharpenn-slate">
                  Already on Sharpenn?{' '}
                  <button
                    type="button"
                    onClick={() => stashAndGo('/login')}
                    className="font-semibold text-brand-warm"
                  >
                    Log in
                  </button>
                </p>
              </>
            )}
          </>
        )}
      </div>

      <p className="mt-6 mb-10 text-[12.5px] text-gray-400 text-center leading-relaxed">
        A free community for beauty professionals.
        <br />
        Jobs, classes, and your people — in one place.
      </p>
    </div>
  )
}

export default JoinCircle
